import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaDownload, FaDatabase, FaPlus, FaClock, FaFileAlt, FaSearch, FaArchive, FaChevronLeft, FaChevronRight, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import API_BASE_URL from '../../components/apiconfig/api-config';
import NoRecordFound from '../../components/common/no-record-found';
import LoadingOverlay from '../../components/common/BackupLoadingOverlay';

const BackupManagement = () => {
  const [backups, setBackups] = useState([]);
  const [fullProjectBackups, setFullProjectBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingFullProject, setLoadingFullProject] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [creatingFullProjectBackup, setCreatingFullProjectBackup] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [search, setSearch] = useState('');
  const [searchFullProject, setSearchFullProject] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [debouncedSearchFullProject, setDebouncedSearchFullProject] = useState('');

  // Loading overlay states
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [loadingSubMessage, setLoadingSubMessage] = useState('');

  // ✅ NEW: Tab state (default to 'database')
  const [activeTab, setActiveTab] = useState('database');

  // ✅ NEW: Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageFullProject, setCurrentPageFullProject] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchFullProject(searchFullProject);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchFullProject]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    setCurrentPageFullProject(1);
  }, [debouncedSearchFullProject]);

  // Extract real creation timestamp from filename or fallback to created_at
  const getBackupTimestamp = (backup) => {
    if (backup.filename) {
      const match = backup.filename.match(/(\d{4}-\d{2}-\d{2})T(\d{2})-(\d{2})-(\d{2})(?:-(\d{3}))?Z?/i);
      if (match) {
        const [, ymd, hh, mm, ss, ms] = match;
        const iso = `${ymd}T${hh}:${mm}:${ss}${ms ? '.' + ms : '.000'}Z`;
        const parsed = new Date(iso);
        if (!isNaN(parsed.getTime())) return parsed.getTime();
      }
    }
    const d = new Date(backup.created_at);
    return !isNaN(d.getTime()) ? d.getTime() : 0;
  };

  // Fetch database backups list
  const fetchBackups = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/backup/list-backups`, {
        withCredentials: true,
      });
      
      if (response.data.success) {
        let filteredBackups = response.data.backups;
        if (debouncedSearch) {
          filteredBackups = filteredBackups.filter(backup => 
            backup.filename.toLowerCase().includes(debouncedSearch.toLowerCase())
          );
        }
        // ✅ Sort by true creation date descending (latest first)
        filteredBackups.sort((a, b) => getBackupTimestamp(b) - getBackupTimestamp(a));
        setBackups(filteredBackups);
      }
    } catch (error) {
      console.error('Error fetching backups:', error);
      toast.error('Failed to fetch backup list');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  // Fetch full project backups list
  const fetchFullProjectBackups = useCallback(async () => {
    try {
      setLoadingFullProject(true);
      const response = await axios.get(`${API_BASE_URL}/api/backup/list-full-project-backups`, {
        withCredentials: true,
      });
      
      if (response.data.success) {
        let filteredBackups = response.data.backups;
        if (debouncedSearchFullProject) {
          filteredBackups = filteredBackups.filter(backup => 
            backup.filename.toLowerCase().includes(debouncedSearchFullProject.toLowerCase())
          );
        }
        // ✅ Sort by true creation date descending (latest first)
        filteredBackups.sort((a, b) => getBackupTimestamp(b) - getBackupTimestamp(a));
        setFullProjectBackups(filteredBackups);
      }
    } catch (error) {
      console.error('Error fetching full project backups:', error);
      toast.error('Failed to fetch full project backup list');
    } finally {
      setLoadingFullProject(false);
    }
  }, [debouncedSearchFullProject]);

  // Create new database backup
  const createBackup = async () => {
    try {
      setCreatingBackup(true);
      setShowLoadingOverlay(true);
      setLoadingMessage('Creating Database Backup');
      setLoadingSubMessage('Exporting database tables and data...');

      const response = await axios.post(`${API_BASE_URL}/api/backup/create-backup`, {}, {
        withCredentials: true,
      });
      
      if (response.data.success) {
        toast.success('Database backup created successfully!');
        setLoadingMessage('Backup Created Successfully!');
        setLoadingSubMessage('Refreshing backup list...');
        
        await fetchBackups();
        
        setTimeout(() => {
          setShowLoadingOverlay(false);
        }, 800);
      }
    } catch (error) {
      console.error('Error creating backup:', error);
      toast.error('Failed to create backup. Please try again.');
      setShowLoadingOverlay(false);
    } finally {
      setCreatingBackup(false);
    }
  };

  // Create full project backup
  const createFullProjectBackup = async () => {
    try {
      setCreatingFullProjectBackup(true);
      setShowLoadingOverlay(true);
      setLoadingMessage('Creating Full Project Backup');
      setLoadingSubMessage('Compressing client and server directories into ZIP file...');

      const response = await axios.post(`${API_BASE_URL}/api/backup/create-full-project-backup`, {}, {
        withCredentials: true,
      });
      
      if (response.data.success) {
        toast.success('Full project backup created successfully!');
        setLoadingMessage('Backup Created Successfully!');
        setLoadingSubMessage('Refreshing backup list...');
        
        await fetchFullProjectBackups();
        
        setTimeout(() => {
          setShowLoadingOverlay(false);
        }, 800);
      }
    } catch (error) {
      console.error('Error creating full project backup:', error);
      toast.error('Failed to create full project backup. Please try again.');
      setShowLoadingOverlay(false);
    } finally {
      setCreatingFullProjectBackup(false);
    }
  };

  // Download database backup file
  const downloadBackup = async (filename) => {
    try {
      setDownloading(filename);
      setShowLoadingOverlay(true);
      setLoadingMessage('Downloading Backup');
      setLoadingSubMessage(`Preparing ${filename} for download...`);

      const response = await axios.get(`${API_BASE_URL}/api/backup/download/${filename}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup downloaded successfully!');
      setShowLoadingOverlay(false);
    } catch (error) {
      console.error('Error downloading backup:', error);
      toast.error('Failed to download backup file');
      setShowLoadingOverlay(false);
    } finally {
      setDownloading(null);
    }
  };

  // Download full project backup file
  const downloadFullProjectBackup = async (filename) => {
    try {
      setDownloading(filename);
      setShowLoadingOverlay(true);
      setLoadingMessage('Downloading Full Project Backup');
      setLoadingSubMessage(`Preparing ${filename} for download...`);

      const response = await axios.get(`${API_BASE_URL}/api/backup/download-full-project/${filename}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Full project backup downloaded successfully!');
      setShowLoadingOverlay(false);
    } catch (error) {
      console.error('Error downloading full project backup:', error);
      toast.error('Failed to download full project backup file');
      setShowLoadingOverlay(false);
    } finally {
      setDownloading(null);
    }
  };

  // Delete database backup file
  const deleteBackup = async (filename) => {
    if (!window.confirm(`Are you sure you want to delete "${filename}"?`)) {
      return;
    }

    try {
      setDeleting(filename);
      const response = await axios.delete(`${API_BASE_URL}/api/backup/delete/${filename}`, {
        withCredentials: true,
      });
      
      if (response.data.success) {
        toast.success('Backup deleted successfully!');
        await fetchBackups();
      }
    } catch (error) {
      console.error('Error deleting backup:', error);
      toast.error('Failed to delete backup file');
    } finally {
      setDeleting(null);
    }
  };

  // Delete full project backup file
  const deleteFullProjectBackup = async (filename) => {
    if (!window.confirm(`Are you sure you want to delete "${filename}"?`)) {
      return;
    }

    try {
      setDeleting(filename);
      const response = await axios.delete(`${API_BASE_URL}/api/backup/delete-full-project/${filename}`, {
        withCredentials: true,
      });
      
      if (response.data.success) {
        toast.success('Full project backup deleted successfully!');
        await fetchFullProjectBackups();
      }
    } catch (error) {
      console.error('Error deleting full project backup:', error);
      toast.error('Failed to delete full project backup file');
    } finally {
      setDeleting(null);
    }
  };

  // Format date using filename as primary source of truth if available
  const formatDate = (dateString, filename) => {
    let date = null;
    if (filename) {
      const match = filename.match(/(\d{4}-\d{2}-\d{2})T(\d{2})-(\d{2})-(\d{2})(?:-(\d{3}))?Z?/i);
      if (match) {
        const [, ymd, hh, mm, ss, ms] = match;
        const iso = `${ymd}T${hh}:${mm}:${ss}${ms ? '.' + ms : '.000'}Z`;
        const parsed = new Date(iso);
        if (!isNaN(parsed.getTime())) date = parsed;
      }
    }
    if (!date) {
      date = new Date(dateString);
    }
    if (isNaN(date.getTime())) return '-';

    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ✅ NEW: Pagination logic for database backups
  const indexOfLastBackup = currentPage * itemsPerPage;
  const indexOfFirstBackup = indexOfLastBackup - itemsPerPage;
  const currentBackups = backups.slice(indexOfFirstBackup, indexOfLastBackup);
  const totalPagesDB = Math.ceil(backups.length / itemsPerPage);

  // ✅ NEW: Pagination logic for full project backups
  const indexOfLastFullProjectBackup = currentPageFullProject * itemsPerPage;
  const indexOfFirstFullProjectBackup = indexOfLastFullProjectBackup - itemsPerPage;
  const currentFullProjectBackups = fullProjectBackups.slice(indexOfFirstFullProjectBackup, indexOfLastFullProjectBackup);
  const totalPagesFP = Math.ceil(fullProjectBackups.length / itemsPerPage);

  // ✅ NEW: Pagination component
  const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex items-center justify-between mt-4 px-3">
        <div className="text-sm text-gray-600">
          Showing {Math.min(indexOfFirstBackup + 1, backups.length)} to {Math.min(indexOfLastBackup, backups.length)} of {backups.length} entries
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-sm"
          >
            <FaChevronLeft size={12} />
            Previous
          </button>

          {startPage > 1 && (
            <>
              <button
                onClick={() => onPageChange(1)}
                className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 text-sm"
              >
                1
              </button>
              {startPage > 2 && <span className="px-2">...</span>}
            </>
          )}

          {pageNumbers.map((number) => (
            <button
              key={number}
              onClick={() => onPageChange(number)}
              className={`px-3 py-1 border rounded text-sm ${
                currentPage === number
                  ? 'bg-[#3899cf] text-white border-[#3899cf]'
                  : 'border-gray-300 hover:bg-gray-100'
              }`}
            >
              {number}
            </button>
          ))}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <span className="px-2">...</span>}
              <button
                onClick={() => onPageChange(totalPages)}
                className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 text-sm"
              >
                {totalPages}
              </button>
            </>
          )}

          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-sm"
          >
            Next
            <FaChevronRight size={12} />
          </button>
        </div>
      </div>
    );
  };

  // Load backups on component mount
  useEffect(() => {
    fetchBackups();
    fetchFullProjectBackups();
  }, [fetchBackups, fetchFullProjectBackups]);

  return (
    <>
      <LoadingOverlay
        isVisible={showLoadingOverlay}
        message={loadingMessage}
        subMessage={loadingSubMessage}
      />

      <div className="max-w-8xl mx-auto p-2 bg-white rounded border border-gray-100">
        {/* ✅ NEW: Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('database')}
              className={`px-6 py-3 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === 'database'
                  ? 'border-[#3899cf] text-[#3899cf] bg-blue-50'
                  : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              <FaDatabase />
              Database Backup
        
            </button>

            <button
              onClick={() => setActiveTab('fullproject')}
              className={`px-6 py-3 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === 'fullproject'
                  ? 'border-green-600 text-green-600 bg-green-50'
                  : 'border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              <FaArchive />
              Full Project Backup
           
            </button>
          </div>
        </div>

        {/* ✅ Database Backup Tab Content */}
        {activeTab === 'database' && (
          <div>
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
              <h2 className="text-xl font-medium text-gray-800 flex items-center gap-2">
                <FaDatabase className="text-[#3899cf]" />
                Database Backup Management
              </h2>
              <div className="flex gap-1 w-full sm:w-auto justify-end">
                <div className="relative w-full sm:w-48">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search backups..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="border border-gray-200 rounded px-3 pl-9 py-2 focus:outline-none w-full text-base"
                  />
                </div>
                <button
                  className="flex items-center gap-1 bg-[#3899cf] text-white px-3 py-2 text-sm rounded hover:bg-[#0e8dc6] border border-[#10a5e9] font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={createBackup}
                  disabled={creatingBackup}
                >
                  <FaPlus />
                  Create Backup
                </button>
              </div>
            </div>

            {loading ? (
              <div className="text-center text-[#3899cf] py-6 text-base">Loading backups...</div>
            ) : backups.length === 0 ? (
              <NoRecordFound message="No database backup files found." />
            ) : (
              <>
                <div className="overflow-x-auto rounded border border-gray-50">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-3 text-left font-medium whitespace-nowrap">S.No.</th>
                        <th className="px-3 py-3 text-left font-medium whitespace-nowrap">File Name</th>
                        <th className="px-3 py-3 text-left font-medium whitespace-nowrap">Type</th>
                        <th className="px-3 py-3 text-left font-medium whitespace-nowrap">Size</th>
                        <th className="px-3 py-3 text-left font-medium whitespace-nowrap">Created Date</th>
                        <th className="px-3 py-3 text-left font-medium whitespace-nowrap">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentBackups.map((backup, idx) => (
                        <tr key={backup.filename} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-3 py-2 whitespace-nowrap">{indexOfFirstBackup + idx + 1}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <FaFileAlt className="text-[#3899cf]" />
                              {backup.filename}
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {backup.filename.includes('auto') ? (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700 font-medium">
                                Auto
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-[#3899cf] font-medium">
                                Manual
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">{backup.size_formatted}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <FaClock className="text-gray-400" />
                              {formatDate(backup.created_at, backup.filename)}
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => downloadBackup(backup.filename)}
                                disabled={downloading === backup.filename || deleting === backup.filename}
                                className="flex items-center gap-1 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-3 py-1 rounded text-sm"
                              >
                                <FaDownload size={12} />
                                Download
                              </button>
                              <button
                                onClick={() => deleteBackup(backup.filename)}
                                disabled={downloading === backup.filename || deleting === backup.filename}
                                className="flex items-center gap-1 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-3 py-1 rounded text-sm"
                              >
                                <FaTrash size={12} />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPagesDB > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPagesDB}
                    onPageChange={setCurrentPage}
                  />
                )}
              </>
            )}

            {/* Info Card */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <FaDatabase className="text-blue-400 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-[#3899cf] mb-2">Database Backup</h3>
                  <ul className="text-sm text-[#3899cf] space-y-1">
                    <li>• SQL format containing complete database</li>
                    <li>• Automatic daily backups at 6:00 PM</li>
                    <li>• Quick and lightweight</li>
                    <li>• Perfect for data recovery</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ✅ Full Project Backup Tab Content */}
        {activeTab === 'fullproject' && (
          <div>
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
              <h2 className="text-xl font-medium text-gray-800 flex items-center gap-2">
                <FaArchive className="text-green-600" />
                Full Project Backup Management
              </h2>
              <div className="flex gap-1 w-full sm:w-auto justify-end">
                <div className="relative w-full sm:w-48">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search backups..."
                    value={searchFullProject}
                    onChange={(e) => setSearchFullProject(e.target.value)}
                    className="border border-gray-200 rounded px-3 pl-9 py-2 focus:outline-none w-full text-base"
                  />
                </div>
                <button
                  className="flex items-center gap-1 bg-green-600 text-white px-3 py-2 text-sm rounded hover:bg-green-700 border border-green-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={createFullProjectBackup}
                  disabled={creatingFullProjectBackup}
                >
                  <FaPlus />
                  Create Full Backup
                </button>
              </div>
            </div>

            {loadingFullProject ? (
              <div className="text-center text-green-600 py-6 text-base">Loading full project backups...</div>
            ) : fullProjectBackups.length === 0 ? (
              <NoRecordFound message="No full project backup files found." />
            ) : (
              <>
                <div className="overflow-x-auto rounded border border-gray-50">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-3 text-left font-medium whitespace-nowrap">S.No.</th>
                        <th className="px-3 py-3 text-left font-medium whitespace-nowrap">File Name</th>
                        <th className="px-3 py-3 text-left font-medium whitespace-nowrap">Type</th>
                        <th className="px-3 py-3 text-left font-medium whitespace-nowrap">Size</th>
                        <th className="px-3 py-3 text-left font-medium whitespace-nowrap">Created Date</th>
                        <th className="px-3 py-3 text-left font-medium whitespace-nowrap">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentFullProjectBackups.map((backup, idx) => (
                        <tr key={backup.filename} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-3 py-2 whitespace-nowrap">{indexOfFirstFullProjectBackup + idx + 1}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <FaArchive className="text-green-600" />
                              {backup.filename}
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700 font-medium">
                              Full Project
                            </span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">{backup.size_formatted}</td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <FaClock className="text-gray-400" />
                              {formatDate(backup.created_at, backup.filename)}
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => downloadFullProjectBackup(backup.filename)}
                                disabled={downloading === backup.filename || deleting === backup.filename}
                                className="flex items-center gap-1 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-3 py-1 rounded text-sm"
                              >
                                <FaDownload size={12} />
                                Download
                              </button>
                              <button
                                onClick={() => deleteFullProjectBackup(backup.filename)}
                                disabled={downloading === backup.filename || deleting === backup.filename}
                                className="flex items-center gap-1 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-3 py-1 rounded text-sm"
                              >
                                <FaTrash size={12} />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPagesFP > 1 && (
                  <div className="flex items-center justify-between mt-4 px-3">
                    <div className="text-sm text-gray-600">
                      Showing {Math.min(indexOfFirstFullProjectBackup + 1, fullProjectBackups.length)} to {Math.min(indexOfLastFullProjectBackup, fullProjectBackups.length)} of {fullProjectBackups.length} entries
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCurrentPageFullProject(currentPageFullProject - 1)}
                        disabled={currentPageFullProject === 1}
                        className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-sm"
                      >
                        <FaChevronLeft size={12} />
                        Previous
                      </button>

                      {Array.from({ length: totalPagesFP }, (_, i) => i + 1).map((number) => (
                        <button
                          key={number}
                          onClick={() => setCurrentPageFullProject(number)}
                          className={`px-3 py-1 border rounded text-sm ${
                            currentPageFullProject === number
                              ? 'bg-green-600 text-white border-green-600'
                              : 'border-gray-300 hover:bg-gray-100'
                          }`}
                        >
                          {number}
                        </button>
                      ))}

                      <button
                        onClick={() => setCurrentPageFullProject(currentPageFullProject + 1)}
                        disabled={currentPageFullProject === totalPagesFP}
                        className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-sm"
                      >
                        Next
                        <FaChevronRight size={12} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Info Card */}
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-3">
                <FaArchive className="text-green-400 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-green-700 mb-2">Complete Project Backup</h3>
                  <ul className="text-sm text-green-700 space-y-1">
                      <li>• ZIP format containing IMS folders</li>
                  <li>• Automatic monthly backups on last day at 5:30 PM</li>
                  <li>• Includes complete source code (IMS)</li>
                  <li>• Perfect for complete project restoration</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BackupManagement;
